import { u } from '../shared/hardcodedKoEn';
/**
 * 재판정 타이머·게이트 — alert-live/systemd mercury-*-retune.timer 와 동기화.
 * (UTC OnCalendar → KST 표시)
 */
export const KST_TZ = 'Asia/Seoul';

export type WakePattern = { utcHours: number[]; utcMinute: number };

export type RetuneScheduleDef = {
  id: string;
  pattern: WakePattern;
  intervalNote: string;
  runWhen: string;
  /** 게이트 통과 시 대략 소요 (analyser 실측 2026-09-01). */
  runDurationShort: string;
};

/** 업숏 — mercury-upbit-pb-retune.timer */
export const UPBIT_PB_SCHEDULE: RetuneScheduleDef = {
  id: 'upbit_pb',
  pattern: { utcHours: [0, 4, 8, 12, 16, 20], utcMinute: 40 },
  intervalNote: u('4시간마다'),
  runWhen: '', // API run_when (yaml cohort_n/gate_n)
  runDurationShort: u('약 10분'),
};

/** 빗숏 — mercury-bh-pb-retune.timer */
export const BH_PB_SCHEDULE: RetuneScheduleDef = {
  id: 'bh_pb',
  pattern: { utcHours: [0, 4, 8, 12, 16, 20], utcMinute: 50 },
  intervalNote: u('4시간마다'),
  runWhen: '', // API run_when
  runDurationShort: u('약 10분'),
};

/** @deprecated 레인 공용 — 타이머 패턴만. 실행 조건은 API. */
export const PULLBACK_SCHEDULE = UPBIT_PB_SCHEDULE;

/** 청캐 — mercury-liq-retune.timer (17:00·05:00 UTC = 02:00·14:00 KST) */
export const LIQ_SCHEDULE: RetuneScheduleDef = {
  id: 'liq',
  pattern: { utcHours: [17, 5], utcMinute: 0 },
  intervalNote: u('12시간마다'),
  runWhen: '', // API run_when
  runDurationShort: u('약 35분'),
};

/** 편승 — mercury-pump-ride-retune.timer */
export const PUMP_RIDE_SCHEDULE: RetuneScheduleDef = {
  id: 'pr',
  pattern: { utcHours: [0, 4, 8, 12, 16, 20], utcMinute: 10 },
  intervalNote: u('4시간마다'),
  runWhen: '', // API run_when
  runDurationShort: u('약 10~15분'),
};

/** 쌍숏 — mercury-dual-short-retune.timer (2026-09-02) */
export const DUAL_SHORT_SCHEDULE: RetuneScheduleDef = {
  id: 'dual_short',
  pattern: { utcHours: [0, 4, 8, 12, 16, 20], utcMinute: 55 },
  intervalNote: u('4시간마다'),
  runWhen: '', // API run_when
  runDurationShort: u('약 3분'),
};

export function formatKstIso(
  iso: string | null | undefined,
  opts?: { withLabel?: boolean },
): string {
  if (!iso) return '–';
  const s = new Date(iso).toLocaleString('ko-KR', {
    timeZone: KST_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return opts?.withLabel ? `${s} KST` : s;
}

export function formatKstDate(d: Date, withLabel = true): string {
  const s = d.toLocaleString('ko-KR', {
    timeZone: KST_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return withLabel ? `${s} KST` : s;
}

/** KST 시·분만 (타이머 깨움 목록). */
export function formatWakeKstTimes(pattern: WakePattern): string {
  return pattern.utcHours
    .map((h) => {
      const d = new Date(Date.UTC(2000, 0, 1, h, pattern.utcMinute));
      return d.toLocaleString('ko-KR', {
        timeZone: KST_TZ,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    })
    .join(' · ');
}

export function nextWakeFromPattern(pattern: WakePattern, from = new Date()): Date {
  const candidates: Date[] = [];
  for (let day = 0; day < 3; day++) {
    for (const h of pattern.utcHours) {
      const d = new Date(Date.UTC(
        from.getUTCFullYear(),
        from.getUTCMonth(),
        from.getUTCDate() + day,
        h,
        pattern.utcMinute,
        0,
        0,
      ));
      if (d > from) candidates.push(d);
    }
  }
  return candidates.sort((a, b) => a.getTime() - b.getTime())[0] ?? from;
}

export type RetuneGateInfo = {
  at: string;
  ran: boolean;
  reason?: string | null;
  n_candidates?: number | null;
};

export function formatRetuneElapsed(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return '–';
  const s = Math.max(0, Math.floor(sec));
  if (s < 60) return `${s}초`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r > 0 ? `${m}분 ${r}초` : `${m}분`;
}

export function scheduleRowMeta(
  schedule: RetuneScheduleDef,
  computedAt?: string | null,
  lastGate?: RetuneGateInfo | null,
  runWhenOverride?: string | null,
): {
  wakeKst: string;
  runWhen: string;
  lastJudgmentKst: string;
  lastWakeKst: string;
  nextWakeKst: string;
} {
  const judgment = formatKstIso(computedAt, { withLabel: true });
  let lastWake = '–';
  if (lastGate?.at) {
    const tag = lastGate.ran ? '격자 실행' : u('스킵');
    const extra = lastGate.n_candidates != null
      ? ` · 모집단 ${lastGate.n_candidates}건`
      : '';
    lastWake = `${formatKstIso(lastGate.at, { withLabel: true })} · ${tag}${extra}`;
    if (!lastGate.ran && lastGate.reason) {
      lastWake += ` — ${lastGate.reason}`;
    }
  }
  return {
    wakeKst: `${formatWakeKstTimes(schedule.pattern)} (${schedule.intervalNote})`,
    runWhen: runWhenOverride
      ? `${runWhenOverride} (${schedule.runDurationShort})`
      : schedule.runWhen
        ? `${schedule.runWhen} (${schedule.runDurationShort})`
        : schedule.runDurationShort,
    lastJudgmentKst: judgment === '–' ? '–' : judgment,
    lastWakeKst: lastWake,
    nextWakeKst: formatKstDate(nextWakeFromPattern(schedule.pattern)),
  };
}

export function scheduleSummaryLine(
  schedule: RetuneScheduleDef,
  computedAt?: string | null,
  lastGate?: RetuneGateInfo | null,
): string {
  const m = scheduleRowMeta(schedule, computedAt, lastGate);
  return `깨움 ${m.wakeKst} · 실행 ${m.runWhen} · 격자 ${m.lastJudgmentKst} · 깨움 ${m.lastWakeKst} · 다음 ${m.nextWakeKst}`;
}
